import { defineStore } from "pinia";
import { notificationsApi } from "@/api/notifications";
import {
  NOTIFICATION_POLL_MS,
  NOTIFICATION_COUNT_MIN_INTERVAL_MS,
  NOTIFICATION_DROPDOWN_STALE_MS,
  NOTIFICATION_PREFETCH_DEBOUNCE_MS,
  isUnread,
} from "@/utils/notification";
import { portalNotificationRoute } from "@/utils/notificationPortalRoutes";

/** Recent notifications for the bell panel (read + unread) */
const DROPDOWN_PARAMS = { per_page: 15, page: 1 };

export const useNotificationStore = defineStore("notifications", {
  state: () => ({
    unreadCount: 0,
    lastPolledCount: 0,
    lastCountFetchedAt: null,
    dropdownItems: [],
    pageItems: [],
    loadingCount: false,
    listLoading: false,
    pageLoading: false,
    dropdownLoaded: false,
    pageLoaded: false,
    dropdownFetchedAt: null,
    pageFetchedAt: null,
    dropdownError: null,
    pageError: null,
    pollTimer: null,
    prefetchTimer: null,
    countInFlight: false,
    _dropdownFetchPromise: null,
    _pageFetchPromise: null,
    pageMeta: {
      current_page: 1,
      last_page: 1,
      per_page: 15,
      total: 0,
    },
  }),

  getters: {
    hasUnread: (state) => state.unreadCount > 0,
    badgeLabel(state) {
      if (state.unreadCount > 99) return "99+";
      if (state.unreadCount <= 0) return "";
      return String(state.unreadCount);
    },
    hasDropdownCache: (state) => state.dropdownLoaded,
    dropdownInitialLoading(state) {
      return state.listLoading && !state.dropdownLoaded;
    },
    dropdownRefreshing(state) {
      return state.listLoading && state.dropdownLoaded;
    },
    pageInitialLoading(state) {
      return state.pageLoading && !state.pageLoaded;
    },
    pageRefreshing(state) {
      return state.pageLoading && state.pageLoaded;
    },
  },

  actions: {
    isDropdownStale() {
      if (!this.dropdownFetchedAt) return true;
      return Date.now() - this.dropdownFetchedAt > NOTIFICATION_DROPDOWN_STALE_MS;
    },

    cachedDropdownUnread() {
      return this.dropdownItems.filter((n) => isUnread(n)).length;
    },

    needsDropdownRefresh() {
      if (!this.dropdownLoaded) return true;
      if (this.isDropdownStale()) return true;
      if (this.unreadCount > this.cachedDropdownUnread()) return true;
      return false;
    },

    applyListMeta(meta) {
      if (meta?.unread_count != null) {
        this.unreadCount = Number(meta.unread_count);
        this.lastCountFetchedAt = Date.now();
      }
    },

    isUnreadCountFresh() {
      if (!this.lastCountFetchedAt) return false;
      return Date.now() - this.lastCountFetchedAt < NOTIFICATION_COUNT_MIN_INTERVAL_MS;
    },

    schedulePrefetchDropdown() {
      if (this.prefetchTimer) clearTimeout(this.prefetchTimer);
      this.prefetchTimer = setTimeout(() => {
        this.prefetchTimer = null;
        if (document.hidden) return;
        this.fetchDropdownList({ background: true, silent: true });
      }, NOTIFICATION_PREFETCH_DEBOUNCE_MS);
    },

    cancelPrefetch() {
      if (this.prefetchTimer) {
        clearTimeout(this.prefetchTimer);
        this.prefetchTimer = null;
      }
    },

    async fetchUnreadCount({ silent = true, force = false } = {}) {
      if (this.countInFlight) return;
      if (!force && this.isUnreadCountFresh()) return;

      this.countInFlight = true;
      if (!silent) this.loadingCount = true;

      const previousCount = this.unreadCount;

      try {
        const count = await notificationsApi.unreadCount();
        this.unreadCount = count;
        this.lastCountFetchedAt = Date.now();

        if (count > previousCount && !document.hidden) {
          this.schedulePrefetchDropdown();
        }
        this.lastPolledCount = count;
      } catch (e) {
        if (!silent) console.error(e);
      } finally {
        this.countInFlight = false;
        this.loadingCount = false;
      }
    },

    async fetchDropdownList({
      force = false,
      background = false,
      silent = false,
    } = {}) {
      if (this._dropdownFetchPromise) return this._dropdownFetchPromise;

      const hasCache = this.dropdownLoaded;

      if (!force && !background && hasCache && !this.needsDropdownRefresh()) {
        return;
      }

      if (background && !force && hasCache && !this.needsDropdownRefresh()) {
        return;
      }

      const showBlockingLoader = !hasCache && !background && !silent;

      if (showBlockingLoader) {
        this.listLoading = true;
      } else if (background && hasCache) {
        this.listLoading = true;
      }

      if (!background || !hasCache) {
        this.dropdownError = null;
      }

      this._dropdownFetchPromise = (async () => {
        try {
          const { items, meta } = await notificationsApi.list(DROPDOWN_PARAMS);
          this.dropdownItems = items;
          this.dropdownLoaded = true;
          this.dropdownFetchedAt = Date.now();
          this.applyListMeta(meta);
          this.dropdownError = null;
        } catch (e) {
          console.error(e);
          if (!hasCache) this.dropdownError = e;
        } finally {
          this.listLoading = false;
          this._dropdownFetchPromise = null;
        }
      })();

      return this._dropdownFetchPromise;
    },

    /** Open bell: show cache instantly, refresh in background when needed */
    refreshDropdownOnOpen() {
      if (this.dropdownLoaded) {
        if (this.needsDropdownRefresh()) {
          return this.fetchDropdownList({ background: true, force: true, silent: true });
        }
        return;
      }
      return this.fetchDropdownList({ background: false });
    },

    async fetchPageList({ category = null, page = 1, force = false } = {}) {
      if (this._pageFetchPromise) return this._pageFetchPromise;

      const hasCache = this.pageLoaded && this.pageItems.length > 0;

      this.pageLoading = true;
      this.pageError = null;

      if (!hasCache || force) {
        this.pageLoaded = false;
      }

      this._pageFetchPromise = (async () => {
        try {
          const params = { page, per_page: 15 };
          if (category) params.category = category;

          const { items, meta } = await notificationsApi.list(params);
          this.pageItems = items;
          this.pageMeta = {
            current_page: meta?.current_page ?? page,
            last_page: meta?.last_page ?? 1,
            per_page: meta?.per_page ?? 15,
            total: meta?.total ?? items.length,
          };
          this.pageLoaded = true;
          this.pageFetchedAt = Date.now();
          this.applyListMeta(meta);
        } catch (e) {
          console.error(e);
          this.pageError = e;
          if (!this.pageItems.length) this.pageLoaded = false;
        } finally {
          this.pageLoading = false;
          this._pageFetchPromise = null;
        }
      })();

      return this._pageFetchPromise;
    },

    _markReadLocally(id) {
      let wasUnread = false;
      for (const n of [...this.dropdownItems, ...this.pageItems]) {
        if (n.id === id && isUnread(n)) {
          n.read_at = new Date().toISOString();
          wasUnread = true;
        }
      }
      if (wasUnread && this.unreadCount > 0) this.unreadCount -= 1;
      return wasUnread;
    },

    async markRead(id) {
      const wasUnread = this._markReadLocally(id);
      if (!wasUnread) return;

      try {
        await notificationsApi.markRead(id);
      } catch (e) {
        console.error(e);
        this.fetchDropdownList({ background: true, force: true, silent: true });
      }
    },

    async markAllRead() {
      const now = new Date().toISOString();
      this.dropdownItems.forEach((n) => {
        if (isUnread(n)) n.read_at = now;
      });
      this.pageItems.forEach((n) => {
        if (isUnread(n)) n.read_at = now;
      });
      this.unreadCount = 0;

      try {
        await notificationsApi.markAllRead();
      } catch (e) {
        console.error(e);
        this.fetchUnreadCount({ silent: true, force: true });
        this.fetchDropdownList({ background: true, force: true, silent: true });
      }
    },

    async openNotification(notification, router) {
      const path = portalNotificationRoute(notification);
      if (!path || !path.startsWith("/")) return;

      if (isUnread(notification)) {
        this.markRead(notification.id);
      }

      router.push(path);
    },

    startPolling() {
      if (this.pollTimer) return;

      this.fetchUnreadCount({ silent: true }).then(() => {
        if (this.unreadCount > 0) {
          this.schedulePrefetchDropdown();
        }
      });
      this.pollTimer = setInterval(() => {
        if (document.hidden) return;
        this.fetchUnreadCount({ silent: true, force: true });
      }, NOTIFICATION_POLL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.cancelPrefetch();
    },

    reset() {
      this.stopPolling();
      this.unreadCount = 0;
      this.lastPolledCount = 0;
      this.lastCountFetchedAt = null;
      this.dropdownItems = [];
      this.pageItems = [];
      this.loadingCount = false;
      this.listLoading = false;
      this.pageLoading = false;
      this.dropdownLoaded = false;
      this.pageLoaded = false;
      this.dropdownFetchedAt = null;
      this.pageFetchedAt = null;
      this.dropdownError = null;
      this.pageError = null;
      this._dropdownFetchPromise = null;
      this._pageFetchPromise = null;
      this.pageMeta = {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
      };
    },
  },
});
